/**
 * Currency and numeric formatting utilities for credit analysis.
 */

/** Format a number or numeric string as BRL currency (no decimals). Returns '-' for empty/zero by default. */
export const fmtCurrency = (v: string | number | null | undefined, hifenWhenZero = true) => {
    if (v == null || v === '') return '-';
    const n = typeof v === 'string' ? parseFloat(v) : v;
    if (isNaN(n)) return '-';
    if (n === 0 || v === '0.0' || v === '0') {
        if (hifenWhenZero) return '-';
    }
    return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

/** Format a numeric string as-is with pt-BR thousand separators (no division). */
export const fmtMil = (v: string | undefined) => {
    if (!v) return '-';
    const n = parseFloat(v);
    if (isNaN(n)) return '-';
    return n.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

/** Format a numeric string divided by 1000 with pt-BR separators (R$ mil). */
export const fmtMilDiv = (v: string | undefined) => {
    if (!v) return '-';
    const n = parseFloat(v);
    if (isNaN(n)) return '-';
    return (n / 1000).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

/** Format a decimal value as percentage (multiplies by 100). Returns '-' for empty/zero. */
export const fmtPercent = (v: string | undefined) => {
    if (!v || v === '#DIV/0!' || v === '0') return '-';
    const n = parseFloat(v);
    return isNaN(n) ? v : `${(n * 100).toFixed(2)}%`;
};

/**
 * Format a bureau default-rate value to percentage string.
 * Handles two formats:
 * - Decimal/comma (e.g. "0.30" or "2,40") → treated as-is percentage
 * - Integer (e.g. "00137") → Serasa format, divide by 100
 * Returns null if value is undefined, null, or not parseable.
 */
export const fmtPdefPercent = (dr: unknown): string | null => {
    if (dr === undefined || dr === null) return null;
    const raw = String(dr).replace(',', '.');
    const num = parseFloat(raw);
    if (isNaN(num)) return null;
    const pct = raw.includes('.') || String(dr).includes(',') ? num : num / 100;
    return pct.toFixed(2) + '%';
};
